import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Spacings } from '@styles'

import { DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import { useForceUpdate } from '@hooks'
import { showLoader } from '@data/state/action'
import { addCommentInCranReport } from '@data/conn/post/cranReport'
import { getCranReportById } from '@data/conn/get/cranReport'
import { getLatestCommentImagePaths } from '../../../cranCommentImages'
import { CRAN_SECTION_TYPES } from '@constants'

import CranCommentCard from '../Components/CranCommentCard'

// Grey body so the white comment cards stand out against it.
const DrawerBody = styled(DrawerBodyWrapper)`
	background: ${({ theme }) => theme.colors.surface.page};
`

// Each comment sits in its own card, spaced apart.
const FieldWrapper = styled.div`
	& + & {
		margin-top: ${Spacings.SPACING_3B};
	}
`

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		// Documents of the same (last) entry the comment itself comes from.
		documents: getLatestCommentImagePaths(data),
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const _state = {
		total_exposure: {
			label: 'Total Exposure Consolidated',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		clean_exposure: {
			label: 'Clean Exposure (if any visible)',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		exposure_nature: {
			label: 'Nature of Exposure',
			placeholder: 'Enter comments here...',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		bank_nbfc_exposure: {
			label: 'Entity Exposure',
			placeholder: 'Enter Entity Exposure (Bank, NBFC, etc.) or any reason to be deduced',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}

	if (data) {
		_state.total_exposure = dataToForm(_state.total_exposure, data?.total_exposure)
		_state.clean_exposure = dataToForm(_state.clean_exposure, data?.clean_exposure)
		_state.exposure_nature = dataToForm(_state.exposure_nature, data?.exposure_nature)
		_state.bank_nbfc_exposure = dataToForm(_state.bank_nbfc_exposure, data?.bank_nbfc_exposure)
	}
	return _state
}
const ChargesForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.CHARGES])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	// Every change to a comment — its text, its CRAN visibility or its sentiment — is saved with
	// the same endpoint, so the card never holds an unsaved state.
	const saveComment = (key, overrides = {}) => {
		const obj = { ...formState[key], ...overrides }
		const comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
		saveComment(key, { show_in_cran: val })
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
		saveComment(key, { comment_type: value })
	}

	// `value` is the draft the editor just saved; it is also written back to the form so the card
	// shows the new comment immediately, before the refetch lands.
	const addHandler = (key) => (value) => {
		const comment = value ?? formState[key]?.value
		updateState(key)(comment)
		saveComment(key, { value: comment })
	}
	return (
		<>
			<DrawerBody>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.total_exposure?.label}
						placeholder={formState?.total_exposure?.placeholder}
						toggleState={formState?.total_exposure?.comment_type}
						savedDocuments={formState?.total_exposure?.documents}
						textValue={formState?.total_exposure?.value}
						onCheckHandler={updateCheckHandler('total_exposure')}
						checkValue={formState?.total_exposure?.show_in_cran}
						onToggleChange={commentTypeHandler('total_exposure')}
						onButtonClick={addHandler('total_exposure')}
						disabled={formState?.total_exposure?.disabled}
						probeId={probeId}
						commentKey={'total_exposure'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.clean_exposure?.label}
						placeholder={formState?.clean_exposure?.placeholder}
						toggleState={formState?.clean_exposure?.comment_type}
						savedDocuments={formState?.clean_exposure?.documents}
						textValue={formState?.clean_exposure?.value}
						onCheckHandler={updateCheckHandler('clean_exposure')}
						checkValue={formState?.clean_exposure?.show_in_cran}
						onToggleChange={commentTypeHandler('clean_exposure')}
						onButtonClick={addHandler('clean_exposure')}
						disabled={formState?.clean_exposure?.disabled}
						probeId={probeId}
						commentKey={'clean_exposure'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.exposure_nature?.label}
						placeholder={formState?.exposure_nature?.placeholder}
						toggleState={formState?.exposure_nature?.comment_type}
						savedDocuments={formState?.exposure_nature?.documents}
						textValue={formState?.exposure_nature?.value}
						onCheckHandler={updateCheckHandler('exposure_nature')}
						checkValue={formState?.exposure_nature?.show_in_cran}
						onToggleChange={commentTypeHandler('exposure_nature')}
						onButtonClick={addHandler('exposure_nature')}
						disabled={formState?.exposure_nature?.disabled}
						probeId={probeId}
						commentKey={'exposure_nature'}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<CranCommentCard
						label={formState?.bank_nbfc_exposure?.label}
						placeholder={formState?.bank_nbfc_exposure?.placeholder}
						toggleState={formState?.bank_nbfc_exposure?.comment_type}
						savedDocuments={formState?.bank_nbfc_exposure?.documents}
						textValue={formState?.bank_nbfc_exposure?.value}
						onCheckHandler={updateCheckHandler('bank_nbfc_exposure')}
						checkValue={formState?.bank_nbfc_exposure?.show_in_cran}
						onToggleChange={commentTypeHandler('bank_nbfc_exposure')}
						onButtonClick={addHandler('bank_nbfc_exposure')}
						disabled={formState?.bank_nbfc_exposure?.disabled}
						probeId={probeId}
						commentKey={'bank_nbfc_exposure'}
					/>
				</FieldWrapper>
			</DrawerBody>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default ChargesForm
